import React from 'react';
import {
  Drawer,
  Box,
  IconButton,
  Tooltip,
  Divider,
  Paper,
  TextField,
  Pagination,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  // ListItemSecondaryAction removed (unused)
  Badge,
  Avatar,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Button,
  LinearProgress,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import TollOutlinedIcon from '@mui/icons-material/TollOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Skeleton from '@mui/material/Skeleton';
import { Link } from 'react-router-dom';
import { useUser } from './WebSocketContext';
import { useTranslation } from 'react-i18next';
import logo from '../images/logo-7-80x80.png';
import ActiveTasksStatus from './GlobalSettings/ActiveTasksStatus';
import CreateMenuButton from './CreateMenuButton';
import FolderIcon from '@mui/icons-material/Folder';
import { getProjectNavigationPath } from '../Utils/projectNavigation';
import OrganizationSwitcher from './OrganizationSwitcher';
import OrganizationLink from './OrganizationLink';
import SidebarAccountMenu from './SidebarAccountMenu';
import CloseButton from './Common/CloseButton';
import PlatformVersion from './PlatformVersion';

/**
 * SidebarDrawer Component
 * 
 * The main sidebar drawer component that contains the navigation, search, and case list.
 * This component provides the primary navigation interface for the application.
 * 
 * Features:
 * - Responsive drawer that can be toggled open/closed
 * - Search functionality for filtering cases
 * - Pagination for large case lists
 * - Language switcher and settings access
 * - Case list with hover effects and context menus
 * - New events notification badges
 * - Smooth animations and modern styling
 */
const SidebarDrawer = ({
  isOpen,
  onToggle,
  cases,
  highlightCaseId = null,
  collections = [],
  collectionsLoading = false,
  selectedCollection,
  onCollectionChange,
  selectedCaseId,
  searchQuery,
  onSearchChange,
  currentPage,
  totalPages,
  onPageChange,
  onCaseClick,
  onCaseNavigationRequest = () => true,
  onCaseMenuOpen,
  onToggleCaseIngestionPause = () => { },
  onLanguageMenuOpen,
  onLogout,
  isUserLoggedIn,
  isPlatformAdmin = false,
  isLoading = false,
  connectionError = false,
  onRetry,
  onCreateProject,
  onCreateCollection,
  primaryColor = '#5757b0',
  hoverColor = '#7777d0',
  selectedColor = '#4545a0',
  lightPurple = '#f0f0ff',
  storageStatus = {},
  serverVersion,
  restrictToUserFolder = false,
  storageDisplayPath = '/',
  storageFolders = [],
  storageFiles = [],
  storageBrowserLoading = false,
  onStorageFolderClick = () => { },
  onStorageNavigateUp = () => { },
  onStorageRefresh = () => { },
  onStorageOpen = () => { },
  credits = 0,
  tier = '',
  tokenPurchasable = true,
  canValidate = false,
  validationMode = 'normal',
  onValidationModeChange = () => { }
}) => {
  const { t, i18n } = useTranslation();
  const { isReadOnly, isValidator, permissions, validationOnly } = useUser();
  const isAdmin = permissions === 'admin';
  const canAccessGlobalSettings = permissions === 'admin' || permissions === 'editor';
  const isCreditBalanceLow = Number(credits) < 100;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [statusDialogOpen, setStatusDialogOpen] = React.useState(false);
  const [collectionMenuAnchor, setCollectionMenuAnchor] = React.useState(null);
  const locale = i18n?.resolvedLanguage || i18n?.language || 'en';
  const creditBalance = Number(credits) || 0;
  const exactCreditBalance = new Intl.NumberFormat(locale).format(creditBalance);
  const compactCreditBalance = new Intl.NumberFormat(locale, {
    notation: creditBalance >= 10000 ? 'compact' : 'standard', maximumFractionDigits: 1
  }).format(creditBalance);
  const relativeTimeFormatter = React.useMemo(
    () => new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'short' }),
    [locale]
  );
  const parseTimestamp = (value) => {
    if (!value) {
      return null;
    }
    const normalizedValue = typeof value === 'string'
      ? value.replace(' ', 'T')
      : value;
    const parsedDate = new Date(normalizedValue);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  };

  const formatRelativeTime = (value, emptyLabel) => {
    const dateValue = parseTimestamp(value);
    if (!dateValue) {
      return emptyLabel;
    }
    const diffMs = Date.now() - dateValue.getTime();
    if (diffMs < 60000) {
      return t('sidebar.just_now', 'Just now');
    }
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 60) {
      return relativeTimeFormatter.format(-minutes, 'minute');
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return relativeTimeFormatter.format(-hours, 'hour');
    }
    const days = Math.floor(hours / 24);
    if (days < 7) {
      return relativeTimeFormatter.format(-days, 'day');
    }
    return dateValue.toLocaleDateString(locale);
  };

  const formatExactTimestamp = (value, emptyLabel) => {
    const dateValue = parseTimestamp(value);
    return dateValue ? dateValue.toLocaleString(locale) : emptyLabel;
  };

  const formatBytes = (bytes) => {
    const parsed = Number(bytes);
    if (!parsed || parsed <= 0) {
      return '0 B';
    }
    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    const exponent = Math.min(Math.floor(Math.log(parsed) / Math.log(1024)), units.length - 1);
    const value = parsed / 1024 ** exponent;
    return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[exponent]}`;
  };

  const getDescriptionPreview = (description) => {
    if (!description || !description.trim()) {
      return '';
    }
    const trimmed = description.trim();
    return trimmed.length > 100 ? `${trimmed.slice(0, 97)}...` : trimmed;
  };

  const getCaseTooltipContent = (caseData, activityDate, activityLabel) => {
    const description = caseData?.description?.trim();
    const goal = caseData?.goal?.trim();
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxWidth: 260 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{caseData.title}</Typography>
        {description && (
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
              {t('sidebar.case_description_label', 'Description')}
            </Typography>
            <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
              {description}
            </Typography>
          </Box>
        )}
        {goal && (
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
              {t('sidebar.case_goal_label', 'Goal')}
            </Typography>
            <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
              {goal}
            </Typography>
          </Box>
        )}
        {activityDate && <Typography variant="caption">
          {activityLabel}: {formatExactTimestamp(activityDate, '')}
        </Typography>}
      </Box>
    );
  };

  // Consolidate theme colors to match other components
  const themeColors = {
    primary: primaryColor,
    primaryLight: hoverColor,
    primaryDark: selectedColor,
    accent: '#f59e0b',
    text: '#374151',
    lightText: '#6b7280',
    background: '#ffffff',
    lightBackground: lightPurple,
    surface: '#ffffff',
    surfaceBorder: '#dfe2ec'
  };

  const storageData = storageStatus || {};
  const rawUsedPercent = storageData.usedPercent ?? storageData.used_percent ?? 0;
  const storageUsedPercent = Math.min(100, Math.max(0, Number(rawUsedPercent) || 0));
  const rawFreePercent = storageData.freePercent ?? storageData.free_percent ?? (100 - storageUsedPercent);
  const storageFreePercent = Math.min(100, Math.max(0, Number(rawFreePercent) || 0));
  const totalBytes = Math.max(0, Number(storageData.totalBytes ?? storageData.total_bytes ?? 0) || 0);
  const freeBytes = Math.max(0, Number(storageData.freeBytes ?? storageData.free_bytes ?? (totalBytes * (storageFreePercent / 100))) || 0);
  const usedBytes = Math.max(0, Number(storageData.usedBytes ?? storageData.used_bytes ?? (totalBytes - freeBytes)) || 0);
  const storageError = storageData.error;
  const isStorageLoading = Boolean(storageData.isLoading);

  // Utilities
  const clearSearch = () => {
    if (typeof onSearchChange !== 'function') return;
    try { onSearchChange({ target: { value: '' } }); } catch (err) { try { onSearchChange(''); } catch (_) { } }
  };
  const handleCaseKeyDown = (e, caseId) => {
    if (e.key === 'Enter' || e.key === ' ') {
      const navigationHandled = onCaseNavigationRequest(caseId, e);
      if (navigationHandled === false) return;
      e.preventDefault();
      onCaseClick(caseId);
      onToggle();
    }
  };

  // limitText removed (UI uses CSS truncation instead)

  // Status dialog handlers
  const handleStatusDialogOpen = () => {
    setStatusDialogOpen(true);
  };

  const handleLogoClick = () => {
    window.location.href = '/';
    if (typeof onToggle === 'function') {
      onToggle();
    }
  };

  const handleStatusDialogClose = () => {
    setStatusDialogOpen(false);
  };

  // Wrapper function to handle create project and close sidebar
  const handleCreateProject = () => {
    onCreateProject(); // Call the original function
    onToggle(); // Close the sidebar
  };

  // Wrapper function to handle create collection and close sidebar
  const handleCreateCollection = () => {
    onCreateCollection(); // Call the original function
    onToggle(); // Close the sidebar
  };

  const selectedCollectionName = selectedCollection
    ? collections.find((collection) => String(collection.id) === String(selectedCollection))?.title
      || t('sidebar.untitled_collection', 'Untitled collection')
    : t('sidebar.all_projects_chip', 'All collections');

  // Avatar helper utilities to generate colored initials for cases (reduces repetitive folder icons)
  // Keep the familiar project colors, with dark initials for readable contrast.
  const avatarColors = ['#b39ddb', '#90caf9', '#a5d6a7', '#ef9a9a', '#ffcc80', '#80cbc4', '#b39ddb', '#ffab91', '#81d4fa', '#f48fb1'];
  const getColorFromString = (input) => {
    const s = String(input || '');
    if (!s) return avatarColors[0];
    let hash = 0;
    for (let i = 0; i < s.length; i++) {
      hash = s.charCodeAt(i) + ((hash << 5) - hash);
      hash = hash & hash;
    }
    return avatarColors[Math.abs(hash) % avatarColors.length];
  };
  // Return two-character initials for better visual balance (e.g. "AB" instead of "A")
  const getInitials = (text) => {
    const s = String(text || '').trim();
    if (!s) return '?';
    const words = s.split(/\s+/).filter(Boolean);
    if (words.length > 1) {
      return (words[0][0] + (words[1][0] || '')).slice(0, 2).toUpperCase();
    }
    return s.slice(0, 2).toUpperCase();
  };
  const getProjectAvatarSx = (title) => ({
    width: 30,
    height: 30,
    fontSize: '0.75rem',
    fontWeight: 600,
    bgcolor: getColorFromString(title),
    color: '#303047'
  });

  return (
    <Drawer
      anchor="left"
      open={isOpen}
      onClose={onToggle}
      ModalProps={{ 'data-allow-backdrop-dismiss': 'true' }}
      PaperProps={{
        sx: {
          width: { xs: '88vw', sm: 320, md: 360, lg: 380 },
          minWidth: { sm: 280 },
          maxWidth: { xs: '92vw', sm: 420 },
          transition: 'width 250ms cubic-bezier(0.4, 0, 0.2, 1)',
          background: '#f6f7fb',
          boxShadow: '8px 0 32px rgba(25,29,65,0.12)',
          border: 'none',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'hidden',
          overflowX: 'hidden',
          boxSizing: 'border-box',
          borderTopRightRadius: 16,
          borderBottomRightRadius: 16,
        }
      }}
    >
      <Box sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        boxSizing: 'border-box',
        overflowX: 'hidden'
      }}>
        {/* Workspace context stays visible; occasional actions live in their menus. */}
        <Box component="header" sx={{ px: 2, py: 1.25, color: '#fff', flexShrink: 0,
          background: `linear-gradient(118deg, ${themeColors.primaryDark}, ${themeColors.primary})` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.25 }}>
            <Button onClick={handleLogoClick} aria-label={t('sidebar.home', 'Home')}
              sx={{ color: 'inherit', p: 0, minWidth: 0, gap: 1.25, textTransform: 'none',
                '&.Mui-focusVisible': { outline: '2px solid #fff', outlineOffset: 4 } }}>
              <Box component="img" src={logo} alt="" sx={{ width: 28, height: 28, objectFit: 'contain' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>DataUnfold</Typography>
            </Button>
            <SidebarAccountMenu isOpen={isOpen} isUserLoggedIn={isUserLoggedIn} isPlatformAdmin={isPlatformAdmin}
              canAccessProcessingActivity={canAccessGlobalSettings} onProcessingActivityOpen={handleStatusDialogOpen}
              onNavigate={onToggle} onLanguageMenuOpen={onLanguageMenuOpen} onLogout={onLogout} tier={tier} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            {isUserLoggedIn && <Box sx={{ flex: 1, minWidth: 0 }}>
              <OrganizationSwitcher primaryColor={themeColors.primary} onNavigate={onToggle} />
            </Box>}
            {isAdmin && (
              <Tooltip arrow title={<Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t('saas.credits_available', 'Available credits')}: {exactCreditBalance}
                </Typography>
                <Typography variant="caption">{
                Number(credits) <= 0
                  ? t(tokenPurchasable ? 'sidebar.credits_empty_purchase' : 'sidebar.credits_empty_organization')
                  : Number(credits) < 100
                    ? t('sidebar.credits_low', { count: Number(credits) })
                    : t('sidebar.credits_description', 'Remaining credits for Dataunfold models')
                }</Typography>
              </Box>}>
                <Box component={tokenPurchasable ? Link : OrganizationLink}
                  to={tokenPurchasable ? '/purchase-credits' : undefined}
                  aria-label={t('saas.credit_balance', { count: Number(credits) || 0 })}
                  onClick={onToggle}
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.625, flexShrink: 0,
                    minHeight: 40, px: 0.75, borderRadius: '8px', textDecoration: 'none',
                    color: isCreditBalanceLow ? '#ffe4a0' : 'inherit',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                    '&:focus-visible': { outline: '2px solid #fff', outlineOffset: 2 } }}>
                  <TollOutlinedIcon aria-hidden="true" sx={{ fontSize: 18, opacity: 0.85 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {compactCreditBalance}
                  </Typography>
                  {creditBalance <= 0 && <ErrorOutlineIcon aria-hidden="true" sx={{ fontSize: 16, color: '#ffb74d' }} />}
                </Box>
              </Tooltip>
            )}
          </Box>
          {isAdmin && creditBalance > 0 && creditBalance < 100 && <Typography role="status" variant="caption" sx={{ display: 'block', px: 1, mt: 0.5 }}>
            {t('sidebar.credits_low', { count: creditBalance })}
          </Typography>}
        </Box>
        {isUserLoggedIn && canValidate && !validationOnly && (
          <Box sx={{ px: 2, pt: 0.75, pb: 0.25, flexShrink: 0 }}>
            <Tabs
              value={validationMode}
              onChange={(_event, nextMode) => onValidationModeChange(nextMode)}
              aria-label={t('sidebar.work_mode', 'Workspace sections')}
              variant="fullWidth"
              sx={{ minHeight: 42, '& .MuiTabs-indicator': { height: 2, borderRadius: 2, bgcolor: themeColors.primary } }}
            >
              <Tab value="normal" label={t('sidebar.normal_mode', 'Projects')} disableRipple
                sx={{ minWidth: 0, minHeight: 42, px: 1, py: 0.75, textTransform: 'none', fontSize: '0.875rem', fontWeight: 600 }} />
              <Tab value="validation" label={t('sidebar.validation_mode', 'Review results')} disableRipple
                sx={{ minWidth: 0, minHeight: 42, px: 1, py: 0.75, textTransform: 'none', fontSize: '0.875rem', fontWeight: 600 }} />
            </Tabs>
          </Box>
        )}
        {isUserLoggedIn && validationOnly && (
          <Box sx={{ px: 2, pt: 1.25, pb: 0.75, flexShrink: 0 }}>
            <Typography variant="body2" sx={{ color: themeColors.primaryDark, fontWeight: 600 }}>
              {t('sidebar.validation_only', 'Result review')}
            </Typography>
          </Box>
        )}

        {/* Collection filtering stays available without looking like a form field. */}
        {permissions !== 'no_access' && <Box sx={{ px: 2, pt: 0.75 }}>
          <Button
            id="project-collection-filter-button"
            fullWidth
            aria-label={`${t('sidebar.filter_by_collection', 'Filter by collection')}: ${selectedCollectionName}`}
            aria-haspopup="menu"
            aria-expanded={Boolean(collectionMenuAnchor)}
            aria-controls={collectionMenuAnchor ? 'project-collection-filter-menu' : undefined}
            onClick={(event) => setCollectionMenuAnchor(event.currentTarget)}
            endIcon={<KeyboardArrowDownIcon />}
            sx={{ justifyContent: 'space-between', minHeight: 36, px: 0, borderRadius: '8px',
              color: themeColors.lightText, textTransform: 'none', fontWeight: 400,
              '&:hover': { bgcolor: 'rgba(87,87,176,0.06)' },
              '&.Mui-focusVisible': { outline: `2px solid ${themeColors.primary}`, outlineOffset: 1 } }}
          >
            <Typography component="span" variant="body2" noWrap sx={{ textAlign: 'left' }}>
              {selectedCollectionName}
            </Typography>
          </Button>
          <Menu
            id="project-collection-filter-menu"
            anchorEl={collectionMenuAnchor}
            open={Boolean(collectionMenuAnchor)}
            onClose={() => setCollectionMenuAnchor(null)}
            MenuListProps={{ 'aria-labelledby': 'project-collection-filter-button' }}
            PaperProps={{ sx: { minWidth: collectionMenuAnchor?.clientWidth, maxWidth: 'calc(100vw - 24px)',
              maxHeight: '60vh', borderRadius: 2, mt: 0.5, '& .MuiMenuItem-root': { minHeight: 44, whiteSpace: 'normal' } } }}
          >
            {canAccessGlobalSettings && (
              <MenuItem onClick={() => { setCollectionMenuAnchor(null); handleCreateCollection(); }}>
                <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
                {t('sidebar.new_collection_option', 'Create collection')}
              </MenuItem>
            )}
            <MenuItem selected={!selectedCollection} onClick={() => { setCollectionMenuAnchor(null); onCollectionChange(null); }}>
              {t('sidebar.all_projects_chip', 'All collections')}
            </MenuItem>
            {collectionsLoading ? (
              <MenuItem disabled>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                {t('sidebar.loading_collections', 'Loading collections...')}
              </MenuItem>
            ) : collections.map((collection) => (
              <MenuItem key={collection.id} selected={String(collection.id) === String(selectedCollection)}
                onClick={() => { setCollectionMenuAnchor(null); onCollectionChange(collection.id); }}>
                {collection.title || t('sidebar.untitled_collection', 'Untitled collection')}
              </MenuItem>
            ))}
          </Menu>
        </Box>}

        {/* Search is the only input in this part of the sidebar. */}
        <Box px={2} py={0.75} sx={{ flexShrink: 0, boxSizing: 'border-box', width: '100%' }}>
          <Paper
            elevation={0}
            sx={{
              p: 0.75,
              display: 'flex',
              alignItems: 'center',
              borderRadius: '12px',
              border: `1px solid ${themeColors.surfaceBorder}`,
              background: themeColors.surface,
              transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
              '&:hover': {
                borderColor: `${themeColors.primary}66`
              },
              '&:focus-within': {
                borderColor: themeColors.primary,
                boxShadow: `0 0 0 2px ${themeColors.primary}22`
              }
            }}
          >
            <TextField
              placeholder={t('sidebar.search_placeholder') || 'Search Topics'}
              size="small"
              value={searchQuery}
              onChange={onSearchChange}
              variant="standard"
              fullWidth
              sx={{
                boxSizing: 'border-box',
                '& .MuiInputBase-input': { fontSize: '0.8125rem' },
                '& .MuiInput-underline:before': { borderBottom: 'none' },
                '& .MuiInput-underline:after': { borderBottom: 'none' },
                '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: 'none' }
              }}
              InputProps={{
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: themeColors.primary, ml: 0.5, fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <IconButton
                    size="small"
                    onClick={clearSearch}
                    aria-label={t('sidebar.clear_search', 'Clear search')}
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '10px',
                      color: themeColors.primary,
                      '&:hover': {
                        backgroundColor: 'rgba(87, 87, 176, 0.08)'
                      }
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ) : null
              }}
            />
          </Paper>
        </Box>



        {/* List of projects with improved styling */}
        <Box sx={{
          flexGrow: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <List sx={{ px: 1, width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
            {/* Create menu moved to footer; top button removed */}

            {(() => {
              if (isLoading) {
                return (
                  <Box sx={{ p: 2 }}>
                    {[...Array(6)].map((_, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        <Skeleton variant="circular" width={28} height={28} />
                        <Skeleton variant="text" width={'70%'} height={16} />
                      </Box>
                    ))}
                  </Box>
                );
              }

              if (connectionError) {
                return (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4, flexDirection: 'column', gap: 2 }}>
                    <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.6)', textAlign: 'center', fontSize: '0.85rem', mb: 1 }}>{t('sidebar.connection_error')}</Typography>
                    <Button variant="outlined" size="small" onClick={onRetry} sx={{ borderColor: primaryColor, color: primaryColor, '&:hover': { borderColor: hoverColor, backgroundColor: 'rgba(87, 87, 176, 0.04)' } }}>{t('sidebar.retry')}</Button>
                  </Box>
                );
              }

              if (cases.length === 0) {
                return (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4, flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.6)', textAlign: 'center', fontSize: '0.85rem' }}>{t('sidebar.no_cases')}</Typography>
                  </Box>
                );
              }

              return cases.map((caseData) => {
                const lastVisit = parseTimestamp(caseData.last_visit);
                const activityDate = lastVisit || parseTimestamp(caseData.created_at);
                const activityLabel = lastVisit ? t('sidebar.last_used', 'Last opened') : t('sidebar.project_created', 'Created');
                const descriptionPreview = getDescriptionPreview(caseData.description);
                const isPendingCopy = Boolean(caseData?.isPendingCopy);
                const isHighlighted = highlightCaseId !== null && String(highlightCaseId) === String(caseData.id);
                const isIngestionPaused = Boolean(caseData?.ingestionPaused);
                const ingestionStatus = String(caseData?.ingestionStatus || (isIngestionPaused ? 'paused' : 'active')).toLowerCase();
                const isIngestionPending = ingestionStatus === 'pending';
                const canToggleIngestion = caseData?.ingestionToggleAllowed !== false && !isIngestionPending;
                return (
                <ListItem
                  key={caseData.id}
                  disablePadding
                  sx={{ mb: 0.25 }}
                >
                  <Tooltip
                    title={getCaseTooltipContent(caseData, activityDate, activityLabel)}
                    describeChild
                    placement="right"
                    arrow
                    enterDelay={400}
                  >
                    <ListItemButton
                      component={isPendingCopy ? 'div' : Link}
                      to={isPendingCopy ? undefined : getProjectNavigationPath(caseData.id, isValidator)}
                      onClick={(clickEvent) => {
                        if (isPendingCopy) return;
                        const navigationHandled = onCaseNavigationRequest(caseData.id, clickEvent);
                        if (navigationHandled === false) return;
                        onCaseClick(caseData.id);
                        onToggle();
                      }}
                      onKeyDown={(e) => {
                        if (isPendingCopy) return;
                        handleCaseKeyDown(e, caseData.id);
                      }}
                      selected={!isPendingCopy && selectedCaseId === caseData.id}
                      disabled={isPendingCopy}
                      sx={{
                        borderRadius: '12px',
                        px: 1,
                        py: 0.75,
                        height: 72,
                        transition: 'background-color 0.15s ease',
                        position: 'relative',
                        ...(isHighlighted ? {
                          backgroundColor: 'rgba(245, 158, 11, 0.15)',
                          boxShadow: `0 0 0 2px ${themeColors.accent}55`,
                          animation: 'caseHighlightPulse 1.6s ease-in-out infinite',
                          '@keyframes caseHighlightPulse': {
                            '0%': { boxShadow: `0 0 0 2px ${themeColors.accent}55` },
                            '50%': { boxShadow: `0 0 0 6px ${themeColors.accent}25` },
                            '100%': { boxShadow: `0 0 0 2px ${themeColors.accent}55` }
                          }
                        } : {}),
                        '&:hover': {
                          backgroundColor: 'rgba(87, 87, 176, 0.06)'
                        },
                        '&.Mui-focusVisible': {
                          outline: `2px solid ${themeColors.primary}`, outlineOffset: -2
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(87, 87, 176, 0.1)',
                          fontWeight: 700,
                          '&:hover': {
                            backgroundColor: 'rgba(87, 87, 176, 0.15)',
                          }
                        }
                      }}
                    >
                      <ListItemAvatar sx={{ minWidth: 44 }}>
                        {caseData.hasNewEvents ? (
                          <Badge variant="dot" color="primary" role="img" aria-label={t('sidebar.new_activity', 'New activity')}
                            sx={{ '& .MuiBadge-dot': { width: 6, minWidth: 6, height: 6, boxShadow: '0 0 0 2px #f6f7fb' } }}>
                            <Avatar aria-hidden="true" sx={getProjectAvatarSx(caseData.title)}>{getInitials(caseData.title)}</Avatar>
                          </Badge>
                        ) : (
                          <Avatar aria-hidden="true" sx={getProjectAvatarSx(caseData.title)}>{getInitials(caseData.title)}</Avatar>
                        )}
                      </ListItemAvatar>

                      <ListItemText
                        primary={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{caseData.title}</Box>
                          {caseData.isPublic === 1 && (
                            <Tooltip title={t('sidebar.public_case', 'Public case (viewable without login)')}>
                              <VisibilityIcon sx={{ color: '#d32f2f', fontSize: '0.95rem' }} />
                            </Tooltip>
                          )}
                        </Box>}
                        primaryTypographyProps={{ fontWeight: selectedCaseId === caseData.id ? 700 : 600, fontSize: '0.8125rem', color: themeColors.text }}
                        secondary={
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.15, fontSize: '0.75rem', color: themeColors.lightText }}>
                            {isPendingCopy && (
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{ display: 'flex', gap: 0.5, alignItems: 'center', lineHeight: 1.1, color: themeColors.accent }}
                              >
                                <CircularProgress size={10} sx={{ color: themeColors.accent }} />
                                {t('sidebar.duplicating', 'Duplicating...')}
                              </Typography>
                            )}
                            {!isPendingCopy && (isIngestionPaused || isIngestionPending) && (
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{ display: 'flex', gap: 0.5, alignItems: 'center', lineHeight: 1.1, color: themeColors.accent }}
                              >
                                <PauseCircleOutlineIcon sx={{ fontSize: '0.85rem' }} />
                                {isIngestionPending
                                  ? t('sidebar.project_pending', 'Pending')
                                  : t('sidebar.project_paused', 'Paused')}
                              </Typography>
                            )}
                            {descriptionPreview && !isPendingCopy && !isIngestionPaused && !isIngestionPending && <Typography
                              component="span"
                              variant="caption"
                              sx={{
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                lineHeight: 1.4,
                                fontSize: '0.6875rem',
                                color: themeColors.lightText
                              }}
                            >
                              {descriptionPreview}
                            </Typography>}
                            {activityDate && <Typography
                              component="span"
                              variant="caption"
                              aria-label={`${activityLabel}: ${formatExactTimestamp(activityDate, '')}`}
                              sx={{ display: 'block', lineHeight: 1.4, fontSize: '0.6875rem', color: themeColors.lightText }}
                            >
                              {formatRelativeTime(activityDate, '')}
                            </Typography>}
                          </Box>
                        }
                        secondaryTypographyProps={{ component: 'div' }}
                        sx={{ minWidth: 0, my: 0 }}
                      />

                      {!isReadOnly && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, alignSelf: 'center' }}>
                          <Tooltip
                            title={
                              isIngestionPending
                                ? t('sidebar.project_pending_toggle_error', 'Finish the project creation flow before changing ingestion state')
                                : (isIngestionPaused
                                    ? t('sidebar.resume_project_ingestion', 'Resume ingestion')
                                    : t('sidebar.pause_project_ingestion', 'Pause ingestion'))
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  onToggleCaseIngestionPause(caseData);
                                }}
                                disabled={isPendingCopy || !canToggleIngestion}
                                aria-label={t(isIngestionPaused ? 'sidebar.resume_project_ingestion' : 'sidebar.pause_project_ingestion')}
                                sx={{ opacity: (isMobile || isIngestionPaused || isIngestionPending) ? 1 : 0,
                                  '.MuiListItemButton-root:hover &, .MuiListItemButton-root:focus-within &': { opacity: 1 } }}
                              >
                                {isIngestionPaused && !isIngestionPending ? (
                                  <PlayArrowIcon fontSize="small" />
                                ) : (
                                  <PauseCircleOutlineIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <IconButton size="small" aria-label={t('sidebar.project_actions', { project: caseData.title })}
                            onClick={(e) => onCaseMenuOpen(e, caseData)} disabled={isPendingCopy}
                            sx={{ opacity: isMobile ? 1 : 0, '.MuiListItemButton-root:hover &, .MuiListItemButton-root:focus-within &': { opacity: 1 } }}>
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              );
              });
            })()}
          </List>
        </Box>

        {/* Footer: Create new project + Pagination */}
        <Divider sx={{ borderColor: '#e3e6ef' }} />
        <Box sx={{ px: 2, py: 1.25, flexShrink: 0, bgcolor: '#fff' }}>
          {!selectedCollection && canAccessGlobalSettings && <Box sx={{ display: 'flex', mb: 1.25,
            '& .MuiButton-root': { minHeight: 40, borderRadius: '12px', mb: 0, boxShadow: 'none', fontWeight: 600,
              '&:hover': { bgcolor: themeColors.primaryDark, transform: 'none', boxShadow: 'none' },
              '&:active': { transform: 'none', boxShadow: 'none' } } }}>
            <CreateMenuButton onCreateProject={handleCreateProject} primaryColor={themeColors.primary} hoverColor={themeColors.primaryDark} />
          </Box>}
          <Box display={'flex'} justifyContent="center">
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={onPageChange}
              size="small"
              color="primary"
              sx={{
                '& .MuiPaginationItem-root': { color: themeColors.lightText, '&.Mui-selected': { backgroundColor: themeColors.lightBackground, color: themeColors.primaryDark, fontWeight: 700 }, '&:hover': { backgroundColor: themeColors.lightBackground }, borderRadius: '8px' }, '& .MuiPagination-ul': { gap: '2px' }
              }}
            />
          </Box>
        </Box>
        <Divider sx={{ borderColor: '#e3e6ef' }} />
        {canAccessGlobalSettings && (
          <Box sx={{ px: 2, pb: 1.25, pt: 1, bgcolor: '#fff' }}>
            {!storageError ? (
              <Box sx={{ opacity: 0.85 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: themeColors.text, fontSize: '0.7rem' }}>
                    {t('sidebar.storage_label', 'STORAGE')}
                  </Typography>
                  <Typography variant="caption" sx={{ color: themeColors.lightText, fontSize: '0.7rem' }}>
                    {isStorageLoading
                      ? '...'
                      : `${formatBytes(usedBytes)} / ${formatBytes(totalBytes)} (${Math.round(storageFreePercent)}% ${t('sidebar.storage_free', 'free')})`}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <LinearProgress
                    variant={isStorageLoading ? 'indeterminate' : 'determinate'}
                    value={storageUsedPercent}
                    sx={{
                      height: '4px',
                      borderRadius: 3,
                      flex: 1,
                      backgroundColor: 'rgba(87, 87, 176, 0.08)',
                      '& .MuiLinearProgress-bar': { backgroundColor: themeColors.primary }
                    }}
                  />
                  {restrictToUserFolder && (
                    <Tooltip title={t('sidebar.storage_open', 'Open File Manager')}>
                      <IconButton
                        size="small"
                        onClick={onStorageOpen}
                        sx={{
                          color: themeColors.primary,
                          width: 32, height: 32, borderRadius: '8px', p: 0.5,
                          '&:hover': {
                            backgroundColor: 'rgba(87, 87, 176, 0.06)'
                          },
                          transition: 'background-color 0.15s ease'
                        }}
                      >
                        <FolderIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            ) : (
              <Typography variant="caption" color="error" sx={{ fontSize: '0.7rem' }}>
                {storageError}
              </Typography>
            )}
          </Box>
        )}
        <PlatformVersion version={serverVersion} />
      </Box>

      {/* Active Tasks Status Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={handleStatusDialogClose}
        maxWidth="md"
        fullWidth
        aria-labelledby="active-tasks-status-title"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(87, 87, 176, 0.3)',
          }
        }}
      >
        <DialogTitle id="active-tasks-status-title" sx={{
          background: `linear-gradient(135deg, ${primaryColor}, ${selectedColor})`,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontWeight: 'bold'
        }}>
          <Box component="span" aria-hidden="true" sx={{ width: 32, flexShrink: 0 }} />
          <Box component="span" sx={{ flex: 1, textAlign: 'center' }}>
            {t('sidebar.processing_activity', 'Processing activity')}
          </Box>
          <CloseButton
            ariaLabel={t('common.close', 'Close')}
            onClick={handleStatusDialogClose}
            sx={{ color: 'inherit' }}
          />
        </DialogTitle>
        <DialogContent sx={{ "--dialog-content-top-padding": "0px", p: 0 }}>
          <ActiveTasksStatus />
        </DialogContent>
      </Dialog>
    </Drawer>
  );
};

export default SidebarDrawer;
